import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPostBySlug, formatDate } from '../data/blogPosts';
import usePageMetadata from '../utils/usePageMetadata';
import { blogPostMetadata } from '../utils/blogMetadata';
import isBlogLink from '../utils/isBlogLink';

const css = `
  .post-root{
    --blue:#0072F5; --blue-dark:#0052B3; --ink:#0B0D10;
    --paper:#F6F4EF; --paper-dim:#ECE8DF; --line:rgba(11,13,16,0.12);
    background:var(--paper); color:var(--ink);
    font-family:'Inter',sans-serif; -webkit-font-smoothing:antialiased;
    min-height:100vh; display:flex; flex-direction:column;
  }
  .post-root *{box-sizing:border-box;}
  .post-root .serif{font-family:'Newsreader',serif; font-weight:400;}
  .post-root a{color:inherit; text-decoration:none;}
  .post-root .wrap{max-width:1240px; margin:0 auto; padding:0 32px;}
  .post-root .measure{max-width:720px; margin:0 auto; padding:0 32px;}

  .post-root header{
    position:fixed; top:0; left:0; right:0; z-index:90;
    background:rgba(246,244,239,0.92); backdrop-filter:blur(10px);
    border-bottom:1px solid var(--line);
  }
  .post-root nav{display:flex; align-items:center; justify-content:space-between; padding:20px 32px; max-width:1240px; margin:0 auto;}
  .post-root .logo{font-weight:800; font-size:18px; letter-spacing:-0.02em;}
  .post-root .logo span{color:var(--blue);}
  .post-root .nav-actions{display:flex; align-items:center; gap:22px;}
  .post-root .nav-login{font-size:14px; font-weight:600; opacity:.75; transition:opacity .2s;}
  .post-root .nav-login:hover{opacity:1;}
  .post-root .nav-cta{font-size:14px; font-weight:600; padding:10px 20px; border:1px solid var(--ink); border-radius:2px; transition:background .2s,color .2s;}
  .post-root .nav-cta:hover{background:var(--ink); color:var(--paper);}
  .post-root .nav-toggle{display:none; background:none; border:none; cursor:pointer; padding:8px; margin-left:8px;}
  .post-root .nav-toggle span{display:block; width:20px; height:2px; background:var(--ink); margin:4px 0;}

  .post-root main{flex:1;}
  .post-root .art-head{padding:140px 32px 34px;}
  .post-root .back{font-size:13.5px; font-weight:600; color:var(--blue-dark); display:flex; width:fit-content; align-items:center; gap:7px; margin-bottom:22px; transition:gap .2s;}
  .post-root .back:hover{gap:12px;}
  .post-root .pill{display:inline-block; font-size:11.5px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; background:var(--paper-dim); padding:5px 11px; border-radius:999px;}
  .post-root h1{font-size:clamp(30px,4.4vw,50px); line-height:1.08; letter-spacing:-0.02em; font-weight:800; margin:18px 0 20px;}
  .post-root .art-meta{font-size:14px; color:#52555a;}
  .post-root .art-meta .sep{padding:0 8px; opacity:.5;}

  .post-root .art-cover{
    position:relative; aspect-ratio:16/7; border-radius:14px; overflow:hidden; margin:14px 0 44px;
    background:linear-gradient(135deg, var(--accent,#0072F5), #0B0D10);
  }
  .post-root .art-cover::after{content:''; position:absolute; inset:0; background-image:radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px); background-size:16px 16px; opacity:.55;}
  .post-root .art-cover-img{position:absolute; inset:0; width:100%; height:100%; object-fit:cover; z-index:1;}

  .post-root .body p{font-size:18px; line-height:1.72; color:#26292e; margin:0 0 24px;}
  .post-root .body .editorial-link{color:var(--blue-dark); text-decoration:underline; text-underline-offset:3px; overflow-wrap:anywhere;}
  .post-root .body h2{font-size:26px; line-height:1.2; letter-spacing:-0.01em; font-weight:800; margin:44px 0 16px;}
  .post-root .body ul{margin:0 0 24px; padding-left:0; list-style:none;}
  .post-root .body li{font-size:18px; line-height:1.6; color:#26292e; padding:8px 0 8px 26px; position:relative;}
  .post-root .body li::before{content:''; position:absolute; left:2px; top:17px; width:7px; height:7px; background:var(--blue); border-radius:50%;}
  .post-root .body blockquote{
    margin:32px 0; padding:6px 0 6px 24px; border-left:3px solid var(--blue);
    font-family:'Newsreader',serif; font-size:23px; line-height:1.4; font-style:italic; color:var(--ink);
  }
  .post-root .body pre{
    background:#0B0D10; color:#eaeef2; border-radius:10px; padding:20px 22px; overflow-x:auto;
    font-family:'Menlo',Monaco,Consolas,monospace; font-size:14px; line-height:1.6; margin:0 0 24px;
  }

  .post-root .art-foot{margin-top:56px; padding-top:32px; border-top:1px solid var(--line); display:flex; justify-content:space-between; align-items:center; gap:16px; flex-wrap:wrap;}
  .post-root .art-foot .byline{font-size:14px; color:#6b6e73;}

  /* not found */
  .post-root .notfound{padding:180px 32px 120px; text-align:center;}
  .post-root .notfound h1{margin-bottom:14px;}
  .post-root .notfound p{font-size:17px; color:#52555a; margin:0 auto 28px; max-width:440px;}

  .post-root footer{padding:64px 0 32px; border-top:1px solid var(--line); margin-top:80px;}
  .post-root .foot-cols{display:grid; grid-template-columns:1.6fr 1fr 1fr 1fr; gap:40px; padding-bottom:48px; border-bottom:1px solid var(--line); margin-bottom:24px;}
  .post-root .foot-brand p{font-size:14px; color:#6b6e73; margin-top:14px; max-width:260px; line-height:1.55;}
  .post-root .foot-col-title{font-size:12px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#6b6e73; margin-bottom:16px;}
  .post-root .foot-col a{display:block; font-size:14px; color:#52555a; padding:6px 0; transition:color .2s;}
  .post-root .foot-col a:hover{color:var(--ink);}
  .post-root .foot-bottom{font-size:13px; color:#6b6e73;}

  @media (max-width:860px){
    .post-root .art-head{padding-top:160px;}
    .post-root .art-cover{aspect-ratio:16/9;}
    .post-root .foot-cols{grid-template-columns:1fr; gap:32px;}
    .post-root nav{flex-wrap:wrap; row-gap:14px;}
    .post-root .nav-toggle{display:flex; flex-direction:column; justify-content:center; gap:5px; order:2; margin-left:auto;}
    .post-root .nav-actions{order:3;}
  }
`;

const renderBlock = (block, i) => {
  switch (block.type) {
    case 'link':
      if (!isBlogLink(block.href)) return <p key={i}>{block.text}</p>;
      return <p key={i}>{block.href.startsWith('/')
        ? <Link className="editorial-link" to={block.href}>{block.text}</Link>
        : <a className="editorial-link" href={block.href}>{block.text}</a>}</p>;
    case 'h2':
      return <h2 key={i}>{block.text}</h2>;
    case 'quote':
      return <blockquote key={i}>{block.text}</blockquote>;
    case 'ul':
      return (
        <ul key={i}>
          {block.items.map((item, j) => <li key={j}>{item}</li>)}
        </ul>
      );
    case 'code':
      return <pre key={i}><code>{block.text}</code></pre>;
    case 'p':
    default:
      return <p key={i}>{block.text}</p>;
  }
};

const Chrome = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="post-root">
      <style>{css}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link
        href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;1,6..72,400&family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <header>
        <nav>
          <Link to="/" className="logo">Data<span>Backfill</span></Link>
          <button className="nav-toggle" aria-label="Toggle menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
            <span></span><span></span><span></span>
          </button>
          <div className="nav-actions">
            <Link to="/blog" className="nav-login">Blog</Link>
            <Link to="/contact" className="nav-cta">Start a Project</Link>
          </div>
        </nav>
      </header>
      <main>{children}</main>
      <footer>
        <div className="wrap foot-cols">
          <div className="foot-brand">
            <div className="logo">Data<span>Backfill</span></div>
            <p>A boutique AI & data delivery studio: senior engineers, architects, and product designers shipping production systems.</p>
          </div>
          <div className="foot-col">
            <div className="foot-col-title">Services</div>
            <Link to="/#services">Claude Implementation</Link>
            <Link to="/#services">RAG & Knowledge Systems</Link>
            <Link to="/#services">Agentic Workflows</Link>
            <Link to="/#services">Data Infrastructure</Link>
          </div>
          <div className="foot-col">
            <div className="foot-col-title">Company</div>
            <Link to="/#work">Case Studies</Link>
            <Link to="/#team">Team</Link>
            <Link to="/#pricing">Pricing</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div className="foot-col">
            <div className="foot-col-title">Use Cases</div>
            <Link to="/use-cases/cmo">25 Use Cases for CMOs</Link>
            <Link to="/use-cases/marketing-ai">25 Marketing AI Use Cases</Link>
            <Link to="/use-cases/ai-bdr">25 AI BDR Use Cases</Link>
            <Link to="/use-cases/cfo">15 Use Cases for CFOs</Link>
            <Link to="/use-cases/customer-support">20 AI Customer Support Use Cases</Link>
            <Link to="/use-cases/revops">12 RevOps AI Use Cases</Link>
            <Link to="/use-cases/hr">10 AI Use Cases for HR Teams</Link>
            <Link to="/use-cases/data-teams">18 Use Cases for Data Teams</Link>
          </div>
        </div>
        <div className="wrap foot-bottom">
          <div>© 2026 DataBackfill. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  usePageMetadata(blogPostMetadata(post, slug));

  if (!post) {
    return (
      <Chrome>
        <div className="wrap notfound">
          <h1>Post not found</h1>
          <p>We couldn't find that article. It may have moved or been unpublished.</p>
          <Link to="/blog" className="nav-cta" style={{ display: 'inline-block' }}>Back to the blog</Link>
        </div>
      </Chrome>
    );
  }

  return (
    <Chrome>
      <article>
        <div className="art-head measure">
          <Link to="/blog" className="back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            All articles
          </Link>
          <span className="pill" style={{ color: post.accent }}>{post.category}</span>
          <h1>{post.title}</h1>
          <div className="art-meta">
            {post.author}<span className="sep">·</span>{formatDate(post.date)}<span className="sep">·</span>{post.readTime} min read
            {post.updatedAt && <><span className="sep">·</span>Updated {formatDate(post.updatedAt)}</>}
          </div>
        </div>

        <div className="measure">
          <div className="art-cover" style={{ '--accent': post.accent }}>
            {post.image && <img className="art-cover-img" src={post.image} alt={post.title} />}
          </div>
        </div>

        <div className="body measure">
          {post.body.map(renderBlock)}

          <div className="art-foot">
            <span className="byline">Written by {post.author}</span>
            <Link to="/contact" className="nav-cta">Start a Project</Link>
          </div>
        </div>
      </article>
    </Chrome>
  );
};

export default BlogPost;
